import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Typography,
  TextField,
  Button,
  Container,
  Box,
  InputAdornment,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import {
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  CloudUpload as CloudUploadIcon,
  TouchApp as TouchAppIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { toWapi } from '../../util/api';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function WhatsAppPictureChecker(props) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [result, setResult] = useState('');
  const [selectBoxOptions, setSelectBoxOptions] = useState([]);
  const [resultStatus, setResultStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [profileImage, setProfileImage] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  useEffect(() => {
    fetchPhoneList();
  }, [props.product]);

  useEffect(() => {
    setIsButtonDisabled(!selectedPhone || !phoneNumber);
  }, [selectedPhone, phoneNumber]);

  const fetchPhoneList = async () => {
    const { product } = props;
    if (product && product.id && product.settings && product.settings.wapi_token) {
      const { id, settings: { wapi_token } } = product;

      try {
        const data = await toWapi(`/api/${id}/listPhones`, wapi_token);
        if (data && data.status === 200 && Array.isArray(data.data)) {
          const activePhones = data.data.filter(phone => phone.status === "active");
          setSelectBoxOptions(activePhones);
        } else {
          console.log('Data is not in the expected format:', data);
        }
      } catch (err) {
        console.error('Error fetching phone list:', err);
      }
    }
  };

  const handleCheck = async (e) => {
    e.preventDefault();
    const { product } = props;
    const { id, settings: { wapi_token } } = product;

    if (!selectedPhone || !phoneNumber) {
      setResult('Please select a phone and enter a number to check.');
      setResultStatus(null);
      setShowResult(true);
      return;
    }

    setIsLoading(true);
    setResult('');
    setResultStatus(null);
    setProfileImage('');
    setShowResult(true);

    try {
      const response = await toWapi(`/api/${id}/${selectedPhone}/getProfileImage?number=${phoneNumber}@c.us`, wapi_token);

      if (response.status === 200) {
        if (response.data && response.data.success && response.data.data.type !== 'error') {
          setProfileImage(response.data.data);
          setResult('Profile picture found!');
          setResultStatus(200);
        } else if (response.data && response.data.data && response.data.data.type === 'error' && response.data.data.code === 'W05') {
          setResult('An error occurred while checking the number. Please check your instance status.');
          setResultStatus(400);
        } else {
          setResult('No profile picture found.');
          setResultStatus(404);
        }
      } else {
        throw new Error('Unexpected response status');
      }
    } catch (error) {
      console.error('Error checking number:', error);
      setResult('An error occurred while checking the number. Please check your instance status.');
      setResultStatus(null);
    } finally {
      setIsLoading(false);
      setShowResult(true);
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setPhoneNumber(value);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, px: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '1600px' }}>
      <Typography variant="h4" gutterBottom align="left" sx={{ fontWeight: 'bold', mb: 1 }}>
        WhatsApp Profile Picture Checker
      </Typography>
      <Typography sx={{ mb: 2 }}>
        The WhatsApp Profile Picture Checker is a user-friendly tool designed to view WhatsApp profile pictures quickly and effortlessly. To use it, simply select one of your active phone numbers, enter the number you want to check, and click the 'Check' button. The tool will promptly process your request and display the profile picture if available.
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Input
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="phone-select-label">Select Active Phone</InputLabel>
              <Select
                labelId="phone-select-label"
                value={selectedPhone}
                onChange={(e) => setSelectedPhone(e.target.value)}
                label="Select Active Phone"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5,
                      width: 250,
                    },
                  },
                }}
              >
                {selectBoxOptions.map((phone) => (
                  <MenuItem key={phone.id} value={phone.id}>
                    {`${phone.id} - ${phone.number}${phone.name ? ` - ${phone.name}` : ''}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              variant="outlined"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              placeholder="Enter a phone number to check"
              InputProps={{
                startAdornment: <InputAdornment position="start">+</InputAdornment>,
              }}
              sx={{ mt: 2 }}
              type="tel"
              inputMode="numeric"
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleCheck}
              disabled={isLoading || isButtonDisabled}
              startIcon={<WhatsAppIcon />}
              sx={{ mt: 2 }}
            >
              {isLoading ? 'Checking...' : 'Check Number'}
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              How to Use
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Select one of your active phone numbers from the dropdown" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CloudUploadIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Enter the phone number you want to check (including country code)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <TouchAppIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Click the 'Check' button" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <VisibilityIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="View the profile picture in the section below" />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
      {showResult && (
        <Paper elevation={3} sx={{ mt: 3, p: 3 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
              <CircularProgress />
            </Box>
          ) : result ? (
            <Box>
              <Alert
                severity={resultStatus === 200 ? "success" : "error"}
                sx={{ mb: 2, backgroundColor: resultStatus === 200 ? '#1DE9B6' : '#FF5252', color: '#000' }}
              >
                {result}
              </Alert>
              {profileImage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Avatar
                    src={profileImage}
                    alt="WhatsApp Profile Picture"
                    sx={{ width: 200, height: 200, border: '4px solid #25D366' }}
                  />
                </Box>
              )}
            </Box>
          ) : null}
        </Paper>
      )}
    </Container>
  );
}

const stateToProps = (state) => ({
  access: state.access,
  location: state.router.location,
  product: state.product.maytapi,
});

export default connect(stateToProps)(WhatsAppPictureChecker);